import {
  collection,
  CollectionReference,
  deleteDoc,
  deleteField,
  doc,
  DocumentData,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  runTransaction,
  setDoc,
  Transaction,
  UpdateData,
  updateDoc
} from 'firebase/firestore';

import { StorageResolver } from '@/firebase/firestore/storage-resolver';
import { Novel, NovelStorageLayout } from '@/lib/types';

export function stripUndefined(value: unknown): unknown {
  if (value === undefined) return undefined;

  if (Array.isArray(value)) {
    return value
      .map(stripUndefined)
      .filter((item) => item !== undefined);
  }

  if (
    value &&
    typeof value === 'object' &&
    Object.getPrototypeOf(value) === Object.prototype
  ) {
    const cleanObject: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
      const cleanValue = stripUndefined(nestedValue);
      if (cleanValue !== undefined) cleanObject[key] = cleanValue;
    });
    return cleanObject;
  }

  return value;
}

/**
 * Base Data Access Object for frontend Firestore interactions.
 * Centralizes layout-aware path resolution and atomic operations.
 */
export abstract class BaseDao {
  public resolver: StorageResolver;

  constructor(public firestore: Firestore) {
    this.resolver = new StorageResolver(firestore);
  }

  /**
   * Resolves a document or collection reference.
   */
  getRef(mode: 'item' | 'collection' | 'bucket', userId: string, novelId: string, family: 'entities' | 'narrative', type: string, itemId: string = '', layout: NovelStorageLayout): DocumentReference | CollectionReference | null {
    if (mode === 'item') return this.resolver.resolveItem(userId, novelId, family, type, itemId, layout).ref;
    if (mode === 'collection') {
      const mode = (family === 'entities' ? layout.entities : layout.narrative)[type]?.items || 'embedded';
      return mode === 'split' ? collection(this.resolver.getNovelRef(userId, novelId), type) : null;
    }
    return this.resolver.getBucketRef(userId, novelId, family, type);
  }

  /**
   * Helper to execute a transaction.
   */
  async withTransaction<T>(fn: (transaction: Transaction) => Promise<T>): Promise<T> {
    return runTransaction(this.firestore, fn);
  }

  /**
   * Reads a full item from Firestore, abstracting away the storage mode.
   */
  async getItem<T>(userId: string, novel: Novel, family: 'entities' | 'narrative', type: string, itemId: string): Promise<(T & { id: string }) | null> {
    const { ref, fieldPath } = this.resolver.resolveItem(userId, novel.id, family, type, itemId, novel.storageLayout);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    if (fieldPath) {
      const item = (snap.data() as { entityList?: Record<string, T> }).entityList?.[itemId];
      return item ? { ...item, id: itemId } : null;
    }
    return { ...(snap.data() as T), id: itemId };
  }

  /**
   * Subscribes to a full item from Firestore, abstracting away the storage mode.
   */
  subscribeItem<T>(
    userId: string, 
    novel: Novel, 
    family: 'entities' | 'narrative', 
    type: string, 
    itemId: string,
    onUpdate: (doc: (T & { id: string }) | null) => void,
    onError: (err: Error) => void
  ): () => void {
    const { ref, fieldPath } = this.resolver.resolveItem(userId, novel.id, family, type, itemId, novel.storageLayout);
    
    return onSnapshot(
      ref,
      (snap: any) => {
        if (!snap.exists()) {
          onUpdate(null);
          return;
        }
        if (fieldPath) {
          const item = (snap.data() as { entityList?: Record<string, T> }).entityList?.[itemId];
          onUpdate(item ? { ...item, id: itemId } as T & { id: string } : null);
        } else {
          onUpdate({ ...(snap.data() as T), id: itemId } as T & { id: string });
        }
      },
      onError
    );
  }

  /**
   * Reads every full item of one type, abstracting away the storage mode.
   */
  async getItems<T extends object>(
    userId: string,
    novel: Novel,
    family: 'entities' | 'narrative',
    type: string
  ): Promise<Array<T & { id: string }>> {
    const layout = family === 'entities' ? novel.storageLayout.entities : novel.storageLayout.narrative;
    const mode = layout[type]?.items || 'embedded';

    if (mode === 'split') {
      const ref = collection(this.resolver.getNovelRef(userId, novel.id), type);
      const snap = await getDocs(ref);
      return snap.docs.map((itemSnap) => ({
        ...(itemSnap.data() as T),
        id: itemSnap.id,
      }));
    }

    const ref = this.resolver.getBucketRef(userId, novel.id, family, type);
    const snap = await getDoc(ref);
    if (!snap.exists()) return [];

    const items = (snap.data() as { entityList?: Record<string, T> }).entityList || {};
    return Object.entries(items).map(([id, item]) => ({ ...item, id }));
  }

  /**
   * Helper to convert a dotted path and updates into a nested object structure.
   */
  nestPayload(fieldPath: string, updates: Record<string, unknown>): Record<string, unknown> {
    const parts = fieldPath.split('.');
    const result: Record<string, unknown> = {};
    let current = result;
    for (let i = 0; i < parts.length - 1; i++) {
      const next: Record<string, unknown> = {};
      current[parts[i]] = next;
      current = next;
    }
    current[parts[parts.length - 1]] = updates;
    return result;
  }

  /**
   * Updates an item in Firestore. Handles both embedded and split modes.
   */
  async updateItem(userId: string, novel: Novel, family: 'entities' | 'narrative', type: string, itemId: string, updates: Record<string, unknown>, transaction?: Transaction): Promise<void> {
    const { ref, fieldPath } = this.resolver.resolveItem(userId, novel.id, family, type, itemId, novel.storageLayout);
    const finalUpdates: Record<string, unknown> = {};
    Object.entries(updates).forEach(([key, value]) => {
      const cleanValue = stripUndefined(value);
      if (cleanValue !== undefined) finalUpdates[fieldPath ? `${fieldPath}.${key}` : key] = cleanValue;
    });

    if (transaction) {
      transaction.update(ref, finalUpdates as UpdateData<DocumentData>);
    } else {
      try {
        await updateDoc(ref, finalUpdates as UpdateData<DocumentData>);
      } catch {
        if (fieldPath) {
          const docData: Record<string, unknown> = {};
          Object.entries(updates).forEach(([key, value]) => {
            const cleanValue = stripUndefined(value);
            if (cleanValue !== undefined) docData[key] = cleanValue;
          });
          await setDoc(ref, { entityList: { [itemId]: docData } }, { merge: true });
        } else {
          await setDoc(ref, stripUndefined(updates) as Record<string, unknown>, { merge: true });
        }
      }
    }
  }

  /**
   * Upserts an item.
   */
  async setItem<T extends { id: string }>(userId: string, novel: Novel, family: 'entities' | 'narrative', type: string, item: T, transaction?: Transaction): Promise<void> {
    const { ref, fieldPath } = this.resolver.resolveItem(userId, novel.id, family, type, item.id, novel.storageLayout);
    const cleanItem = stripUndefined(item) as T;
    if (fieldPath) {
      const updates = { [fieldPath]: cleanItem };
      if (transaction) {
        transaction.update(ref, updates as UpdateData<DocumentData>);
      } else {
        try {
          await updateDoc(ref, updates as UpdateData<DocumentData>);
        } catch {
          await setDoc(ref, { entityList: { [item.id]: cleanItem } }, { merge: true });
        }
      }
    } else {
      if (transaction) transaction.set(ref, cleanItem, { merge: true });
      else await setDoc(ref, cleanItem, { merge: true });
    }
  }

  /**
   * Deletes an item.
   */
  async deleteItem(userId: string, novel: Novel, family: 'entities' | 'narrative', type: string, itemId: string, transaction?: Transaction): Promise<void> {
    const { ref, fieldPath } = this.resolver.resolveItem(userId, novel.id, family, type, itemId, novel.storageLayout);
    if (fieldPath) {
      const updates = { [fieldPath]: deleteField() };
      if (transaction) {
        transaction.update(ref, updates as UpdateData<DocumentData>);
      } else {
        try {
          await updateDoc(ref, updates as UpdateData<DocumentData>);
        } catch {
          await setDoc(ref, { entityList: { [itemId]: deleteField() } }, { merge: true });
        }
      }
    } else {
      if (transaction) transaction.delete(ref);
      else await deleteDoc(ref);
    }
  }

  /**
   * Batch updates summaries in metadata documents.
   */
  async batchUpdateSummaries(userId: string, novel: Novel, family: 'entities' | 'narrative', type: string, summaries: Record<string, unknown | null>): Promise<void> {
    const bucketUpdates: Record<string, Record<string, unknown>> = {};
    const layout = novel.storageLayout;

    for (const [itemId, summary] of Object.entries(summaries)) {
      const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, family, type, itemId, layout);
      const refPath = ref.path;
      if (!bucketUpdates[refPath]) bucketUpdates[refPath] = {};
      bucketUpdates[refPath][fieldPath as string] = summary || deleteField();
    }

    await Promise.all(Object.entries(bucketUpdates).map(async ([path, updates]) => {
      const ref = doc(this.firestore, path);
      try {
        await updateDoc(ref, updates as UpdateData<DocumentData>);
      } catch {
        const nested: Record<string, unknown> = {};
        Object.entries(updates).forEach(([key, value]) => {
          const parts = key.split('.');
          let current = nested;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!(parts[i] in current) || typeof current[parts[i]] !== 'object' || current[parts[i]] === null) {
              current[parts[i]] = {};
            }
            current = current[parts[i]] as Record<string, unknown>;
          }
          current[parts[parts.length - 1]] = value;
        });
        await setDoc(ref, nested, { merge: true });
      }
    }));
  }

  /**
   * Batch saves multiple items.
   */
  async batchSaveItems<T extends { id: string }>(userId: string, novel: Novel, family: 'entities' | 'narrative', type: string, items: T[]): Promise<void> {
    const layout = novel.storageLayout;
    const mode = (family === 'entities' ? layout.entities : layout.narrative)[type]?.items || 'embedded';

    if (mode === 'embedded') {
      const ref = this.resolver.getBucketRef(userId, novel.id, family, type);
      const updates: Record<string, unknown> = {};
      items.forEach(item => {
        Object.entries(item).forEach(([key, value]) => {
          const cleanValue = stripUndefined(value);
          if (key === 'id' || cleanValue === undefined) return;
          updates[`entityList.${item.id}.${key}`] = cleanValue;
        });
      });
      try {
        await updateDoc(ref, updates as UpdateData<DocumentData>);
      } catch {
        const initialData: Record<string, unknown> = {};
        items.forEach(item => {
          initialData[item.id] = stripUndefined(item);
        });
        await setDoc(ref, { entityList: initialData }, { merge: true });
      }
    } else {
      await this.withTransaction(async (transaction) => {
        items.forEach(item => {
          const ref = this.resolver.resolveItem(userId, novel.id, family, type, item.id, layout).ref;
          transaction.set(ref, stripUndefined(item) as T, { merge: true });
        });
      });
    }
  }
}
